/* Product gallery - thumbnail to hero image.
 *
 * The markup and the styling for this were always in place: the thumbnails
 * render as <button data-gallery-thumb> and theme.css already styles
 * `.pdp-gallery-thumb.active` with the selected outline. What never existed was
 * a click handler, so every thumbnail was inert and the hero image stayed on
 * the first photo for the life of the page, on every product. This is the
 * missing piece.
 *
 * The thumbnail carries its picture as a CSS background-image (at width=240)
 * and its data-gallery-thumb attribute is empty, so the full-size URL is
 * derived by rewriting Shopify's `width` parameter. That means this works on
 * every product immediately, with no change to the product template and no
 * per-product data - which is what makes it safe to ship across the range.
 *
 * Selected state is DERIVED, not remembered: it is recomputed from whichever
 * image the hero is actually showing, and re-runs whenever the hero's src
 * changes. Another script on the page rewrites gallery classes shortly after
 * load (the gallery filters itself to the selected variant), which wiped a
 * one-shot marker; deriving it means the outline cannot drift out of sync with
 * the picture, whoever changed it.
 *
 * Delegated from the document so it survives the gallery being re-rendered.
 * Progressive enhancement: with no JS the first image still renders and the
 * page is still shoppable.
 */
(function () {
  var SRCSET_WIDTHS = [600, 1000, 1400, 2000];
  var SRC_WIDTH = 1400;

  function galleries() {
    return document.querySelectorAll('.pdp-gallery');
  }

  function heroOf(gallery) {
    return gallery.querySelector('[data-gallery-hero] img') ||
           gallery.querySelector('.pdp-gallery-img');
  }

  /* Pull the image URL out of a thumbnail. An explicit data-gallery-thumb
     value wins, so if the template is ever taught to emit one this keeps
     working and gets more accurate. Otherwise read the background-image. */
  function thumbUrl(btn) {
    var explicit = btn.getAttribute('data-gallery-thumb');
    if (explicit) return explicit;

    var bg = btn.style.backgroundImage;
    if (!bg || bg === 'none') bg = window.getComputedStyle(btn).backgroundImage;
    if (!bg || bg === 'none') return null;

    var m = bg.match(/url\((['"]?)(.*?)\1\)/);
    return m ? m[2] : null;
  }

  /* Re-request the same asset at a different size. Shopify serves both the
     query-parameter form (…?v=123&width=240) and the older filename form
     (…_240x.jpg); handle each, and leave anything unrecognised alone rather
     than mangling it. */
  function atWidth(url, w) {
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + w);
    if (/_\d+x\./.test(url)) return url.replace(/_\d+x\./, '_' + w + 'x.');
    return url;
  }

  /* Identity of an image regardless of the size it was requested at, so a
     240px thumbnail can be matched against a 1400px hero. */
  function imageKey(url) {
    if (!url) return '';
    return url.split('?')[0].replace(/_\d+x(?=\.)/, '');
  }

  /* Recompute which thumbnail is selected from what the hero is showing. */
  function syncActive(gallery) {
    var hero = heroOf(gallery);
    if (!hero) return;

    var key = imageKey(hero.getAttribute('src'));
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!thumbs.length) return;

    var matched = null;
    thumbs.forEach(function (t) {
      if (!matched && imageKey(thumbUrl(t)) === key) matched = t;
    });

    /* If the hero is showing something no thumbnail offers, fall back to the
       first rather than leaving the strip with nothing selected. */
    var chosen = matched || thumbs[0];

    thumbs.forEach(function (t) {
      var on = t === chosen;
      t.classList.toggle('active', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  function show(gallery, btn) {
    var hero = heroOf(gallery);
    if (!hero) return;

    var url = thumbUrl(btn);
    if (!url) return;

    var full = atWidth(url, SRC_WIDTH);

    /* Only touch srcset when the URL is actually resizable. If atWidth could
       not rewrite it, a srcset of four identical URLs would tell the browser
       the same file is four different sizes. */
    if (full !== url) {
      hero.srcset = SRCSET_WIDTHS.map(function (w) {
        return atWidth(url, w) + ' ' + w + 'w';
      }).join(', ');
    } else {
      hero.removeAttribute('srcset');
    }
    hero.src = full;

    /* The hero is no longer the image the server rendered, so let the browser
       fetch it eagerly rather than deferring. */
    hero.loading = 'eager';

    syncActive(gallery);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-gallery-thumb]');
    if (!btn) return;
    var gallery = btn.closest('.pdp-gallery');
    if (!gallery) return;
    e.preventDefault();
    show(gallery, btn);
  });

  /* Arrow keys walk the strip, which is what a keyboard user expects from a
     row of image choices. Focus moves with the selection so the outline and
     the focus ring stay together. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' &&
        e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

    var btn = e.target.closest && e.target.closest('[data-gallery-thumb]');
    if (!btn) return;
    var gallery = btn.closest('.pdp-gallery');
    if (!gallery) return;

    var all = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
    var i = all.indexOf(btn);
    if (i === -1) return;

    var step = (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 1;
    var next = all[(i + step + all.length) % all.length];

    e.preventDefault();
    next.focus();
    show(gallery, next);
  });

  function start() {
    galleries().forEach(function (gallery) {
      syncActive(gallery);

      /* Whoever changes the hero - this script, a variant change, anything
         else - the strip follows. */
      var hero = heroOf(gallery);
      if (!hero || !window.MutationObserver) return;
      new MutationObserver(function () { syncActive(gallery); })
        .observe(hero, { attributes: true, attributeFilter: ['src'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  /* Late-running theme scripts settle after load; re-derive once more so the
     initial outline is correct whatever they did on the way through. */
  window.addEventListener('load', function () {
    galleries().forEach(syncActive);
  });
})();